import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", username: "", license_id: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [discord, setDiscord] = useState(false);
  const [showEmail, setShowEmail] = useState(true);
  const [staff, setStaff] = useState(false);

  useEffect(() => {
    if (user) nav(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    const isStaff = new URLSearchParams(window.location.search).get("staff") === "1";
    setStaff(isStaff);
    api.get("/auth/discord/config")
      .then(({ data }) => {
        setDiscord(data.enabled);
        setShowEmail(isStaff || !data.enabled);
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u =
        mode === "login"
          ? await login(form.email, form.password)
          : await register({
              email: form.email,
              password: form.password,
              username: form.username,
              license_id: form.license_id,
            });
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const discordLogin = async () => {
    try {
      const { data } = await api.get("/auth/discord/login");
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      <div className="relative hidden overflow-hidden border-r border-border md:block">
        <img
          src="https://images.unsplash.com/photo-1672872476232-da16b45c9001?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxuZW9uJTIwY2l0eSUyMG5pZ2h0fGVufDB8fHx8MTc4NTk2MjY5NXww&ixlib=rb-4.1.0&q=85"
          alt="Neon city"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-background/85" />
        <div className="absolute inset-0 drip" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Brand size="lg" />
          <div>
            <h2 className="text-lg md:text-lg font-semibold">Every minute counts.</h2>
            <p className="mt-3 max-w-sm text-sm text-muted-foreground">
              Log in to see your rank, bank your playtime and cash out on perks in the city.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="auth-form">
          <div className="md:hidden mb-10">
            <Brand size="lg" />
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-primary">
            {staff ? "Staff access" : mode === "login" ? "Player access" : "New citizen"}
          </p>
          <h1 className="mt-3 text-4xl font-bold">{mode === "login" ? "Sign in" : "Create account"}</h1>

          {discord && !staff && (
            <div className="mt-8">
              <Button
                type="button"
                onClick={discordLogin}
                className="w-full rounded-none bg-[#5865F2] text-white hover:bg-[#4752c4]"
                data-testid="discord-login-btn"
              >
                Continue with Discord
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">
                Players sign in with Discord. Staff link your in-game character to your account.
              </p>
            </div>
          )}

          {showEmail && (
          <>
          <div className="mt-10 space-y-5">
            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Username</Label>
                <Input
                  data-testid="username-input"
                  className="mt-2 rounded-none bg-secondary"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  required
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
              <Input
                data-testid="email-input"
                type="email"
                className="mt-2 rounded-none bg-secondary"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Password</Label>
              <Input
                data-testid="password-input"
                type="password"
                className="mt-2 rounded-none bg-secondary"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </div>
            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  FiveM license / CFX id (optional)
                </Label>
                <Input
                  data-testid="license-input"
                  className="mt-2 rounded-none bg-secondary mono"
                  placeholder="license:abc123..."
                  value={form.license_id}
                  onChange={(e) => setForm({ ...form, license_id: e.target.value })}
                />
              </div>
            )}
          </div>

          {error && (
            <div data-testid="auth-error" className="mt-5 border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <Button type="submit" disabled={busy} className="mt-8 w-full rounded-none" data-testid="auth-submit-btn">
            {busy ? "Please wait…" : mode === "login" ? "Enter the city" : "Create account"}
          </Button>

          {!staff && (
          <button
            type="button"
            data-testid="auth-toggle-mode"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError("");
            }}
            className="mt-6 w-full text-sm text-muted-foreground hover:text-primary"
          >
            {mode === "login" ? "No account yet? Register" : "Already registered? Sign in"}
          </button>
          )}
          </>
          )}
          <Link to="/" className="mt-4 block text-center text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">
            Back to site
          </Link>
        </form>
      </div>
    </div>
  );
}
